export type TabActivityState = 'generating' | 'complete' | 'error';

const TitlePrefixes: Record<TabActivityState, string> = {
	complete: '✅',
	error: '⚠️',
	generating: '⏳',
};

// Canvas fillStyle cannot resolve var() strings, so THEME.colors tokens are unusable here directly.
// Resolve the same CSS variables THEME points at; the fallbacks mirror palette.css tokens
// --palette-trickster-green-m, --palette-spell-red-m, and --palette-magic-orange-m respectively.
const BadgeColors: Record<TabActivityState, { cssVariable: string; fallback: string }> = {
	complete: { cssVariable: '--color-success-main', fallback: '#99db56' },
	error: { cssVariable: '--color-danger-main', fallback: '#de2139' },
	generating: { cssVariable: '--color-warning-main', fallback: '#ff9d52' },
};

let baseTitle: string | null = null;
let lastAppliedTitle: string | null = null;
let originalFaviconHrefs: Map<HTMLLinkElement, string> | null = null;
// Badging is async (image loads); the token discards stale draws when the state changes mid-flight.
let renderToken = 0;

function resolveBadgeColor(state: TabActivityState) {
	const { cssVariable, fallback } = BadgeColors[state];
	const resolved = window.getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim();
	return resolved || fallback;
}

function drawBadgedIcon(href: string, color: string): Promise<string | null> {
	return new Promise(resolve => {
		const image = new Image();
		image.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = image.naturalWidth;
				canvas.height = image.naturalHeight;
				// SVG icons without intrinsic dimensions report 0x0; badging one would blank the favicon.
				if (canvas.width === 0 || canvas.height === 0) {
					resolve(null);
					return;
				}
				const context = canvas.getContext('2d');
				if (!context) {
					resolve(null);
					return;
				}
				context.drawImage(image, 0, 0);
				const radius = canvas.width * 0.26;
				const center = { x: canvas.width - radius, y: canvas.height - radius };
				context.beginPath();
				context.arc(center.x, center.y, radius, 0, Math.PI * 2);
				context.fillStyle = color;
				context.fill();
				context.lineWidth = Math.max(1, radius * 0.25);
				context.strokeStyle = '#ffffff';
				context.stroke();
				resolve(canvas.toDataURL('image/png'));
			} catch {
				// A tainted canvas (cross-origin favicon) throws on toDataURL; skip the badge.
				resolve(null);
			}
		};
		image.onerror = () => resolve(null);
		image.src = href;
	});
}

function getFaviconLinks(): HTMLLinkElement[] {
	// rel~="icon" matches both rel="icon" and rel="shortcut icon"
	return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
}

async function applyFaviconBadges(state: TabActivityState, token: number) {
	const links = getFaviconLinks();
	if (!originalFaviconHrefs) {
		originalFaviconHrefs = new Map(links.map(link => [link, link.href]));
	}
	const color = resolveBadgeColor(state);
	await Promise.all(
		links.map(async link => {
			const originalHref = originalFaviconHrefs?.get(link);
			if (!originalHref) {
				return;
			}
			const badgedHref = await drawBadgedIcon(originalHref, color);
			if (badgedHref && token === renderToken) {
				link.href = badgedHref;
			}
		})
	);
}

export function showTabActivityIndicator(state: TabActivityState) {
	renderToken++;
	// Re-capture the base title if something else (e.g. account load) has changed it since we last wrote it.
	if (baseTitle === null || document.title !== lastAppliedTitle) {
		baseTitle = document.title;
	}
	lastAppliedTitle = `${TitlePrefixes[state]} ${baseTitle}`;
	document.title = lastAppliedTitle;
	applyFaviconBadges(state, renderToken);
}

export function isTabActivityIndicatorShown() {
	return baseTitle !== null;
}

export function clearTabActivityIndicator() {
	renderToken++;
	if (baseTitle !== null && document.title === lastAppliedTitle) {
		document.title = baseTitle;
	}
	// Restore via a live query rather than iterating the map, so we never write to detached nodes
	// if something else replaced the <link> elements while the indicator was active.
	getFaviconLinks().forEach(link => {
		const originalHref = originalFaviconHrefs?.get(link);
		if (originalHref !== undefined) {
			link.href = originalHref;
		}
	});
	baseTitle = null;
	lastAppliedTitle = null;
	originalFaviconHrefs = null;
}
